import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card, Tabs, Tab, ProgressBar } from "react-bootstrap";
import SafetyIspettivaService from "../../../services/safety/SafetyIspettivaService";

const STATI_CHK = [
    { value: "DA_VERIFICARE", label: "Da verificare" },
    { value: "OK", label: "Conforme" },
    { value: "NON_CONFORME", label: "Non conforme" },
    { value: "NA", label: "Non applicabile" },
];

const TIPI_VERB = [
    { value: "VERIFICA_ISPETTIVA", label: "Verifica ispettiva" },
    { value: "PRESCRIZIONE", label: "Prescrizione" },
    { value: "VERBALE_NC", label: "Verbale NC" },
    { value: "ALTRO", label: "Altro" },
];

const STATI_VERB = [
    { value: "APERTO", label: "Aperto" },
    { value: "IN_CORSO", label: "In corso" },
    { value: "CHIUSO", label: "Chiuso" },
];

const EMPTY_VERB = {
    id_verbale: "",
    tipo: "VERIFICA_ISPETTIVA",
    ente: "",
    data_verbale: "",
    oggetto: "",
    descrizione: "",
    data_scadenza_adeguamento: "",
    stato: "APERTO",
    note: "",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function apiErr(err, fallback) {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    const m = err.message || err.messaggio || err.error?.message || err.error;
    return (typeof m === "string" && m.trim()) ? m : fallback;
}

function statoBadge(stato) {
    const map = {
        DA_VERIFICARE: { bg: "secondary", label: "Da verificare" },
        OK: { bg: "success", label: "Conforme" },
        NON_CONFORME: { bg: "danger", label: "Non conforme" },
        NA: { bg: "info", label: "N/A" },
    };
    const m = map[stato] || map.DA_VERIFICARE;
    return <Badge bg={m.bg}>{m.label}</Badge>;
}

function scadenzaBadge(v) {
    if (v.stato === "CHIUSO") return <Badge bg="success">Chiuso</Badge>;
    const map = {
        SCADUTO: { bg: "danger", label: "Scaduto" },
        IN_SCADENZA: { bg: "warning", label: "In scadenza" },
        VALIDO: { bg: "success", label: "In regola" },
    };
    const m = map[v.stato_scadenza] || { bg: "light", label: "—" };
    return (
        <span>
            <Badge bg={m.bg} text={m.bg === "warning" || m.bg === "light" ? "dark" : "light"} className={m.bg === "light" ? "border" : undefined}>
                {m.label}
            </Badge>
            {typeof v.giorni_alla_scadenza === "number" && v.stato_scadenza !== "VALIDO" && (
                <span className="text-muted small ms-1">
                    ({v.giorni_alla_scadenza < 0 ? `${Math.abs(v.giorni_alla_scadenza)} gg fa` : `tra ${v.giorni_alla_scadenza} gg`})
                </span>
            )}
        </span>
    );
}

export default function IspettivaPanel({ idAzienda, settore }) {
    const [checklist, setChecklist] = useState([]);
    const [stats, setStats] = useState(null);
    const [verbali, setVerbali] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [showVerb, setShowVerb] = useState(false);
    const [formVerb, setFormVerb] = useState(EMPTY_VERB);
    const [fileVerb, setFileVerb] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [editNotes, setEditNotes] = useState({});

    const load = useCallback(async () => {
        if (!idAzienda) return;
        setLoading(true);
        setError(null);
        try {
            const [chkData, verbData] = await Promise.all([
                SafetyIspettivaService.checklistByAzienda(idAzienda),
                SafetyIspettivaService.verbaliByAzienda(idAzienda),
            ]);
            const parsed = chkData && !chkData.message ? chkData : {};
            setChecklist(Array.isArray(parsed.items) ? parsed.items : []);
            setStats(parsed.stats || null);
            setVerbali(Array.isArray(verbData) ? verbData : []);
        } catch (err) {
            setChecklist([]);
            setVerbali([]);
            const m = apiErr(err, "");
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Verifica deploy: SafetyIspettivaController + Service, auth.json, SQL 2026-06-18."
                : (m || "Errore caricamento area ispettiva."));
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { load(); }, [load]);

    const handleSaveCheck = async (item, stato) => {
        setSavingId(item.id_tipo_check);
        setMsg(null);
        setError(null);
        try {
            await SafetyIspettivaService.saveChecklistItem({
                id_azienda: idAzienda,
                id_tipo_check: item.id_tipo_check,
                stato,
                note: editNotes[item.id_tipo_check] ?? item.note ?? "",
                data_verifica: new Date().toISOString().substring(0, 10),
            });
            setMsg("Checklist aggiornata.");
            await load();
        } catch (err) {
            setError(apiErr(err, "Errore salvataggio voce checklist."));
        } finally {
            setSavingId(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        try {
            await SafetyIspettivaService.exportFascicolo(idAzienda, `fascicolo_ispettivo_${idAzienda}.html`);
            setMsg("Fascicolo esportato.");
        } catch (err) {
            setError(apiErr(err, "Errore export fascicolo."));
        } finally {
            setExporting(false);
        }
    };

    const openVerbForm = (v = null) => {
        if (v) {
            setFormVerb({
                id_verbale: v.id_verbale,
                tipo: v.tipo || "VERIFICA_ISPETTIVA",
                ente: v.ente || "",
                data_verbale: v.data_verbale ? String(v.data_verbale).substring(0, 10) : "",
                oggetto: v.oggetto || "",
                descrizione: v.descrizione || "",
                data_scadenza_adeguamento: v.data_scadenza_adeguamento ? String(v.data_scadenza_adeguamento).substring(0, 10) : "",
                stato: v.stato || "APERTO",
                note: v.note || "",
            });
        } else {
            setFormVerb({ ...EMPTY_VERB, data_verbale: new Date().toISOString().substring(0, 10) });
        }
        setFileVerb(null);
        setShowVerb(true);
    };

    const handleSaveVerb = async (e) => {
        e.preventDefault();
        setError(null);
        setMsg(null);
        const fd = new FormData();
        fd.append("id_azienda", idAzienda);
        Object.entries(formVerb).forEach(([k, v]) => {
            if (v !== "" && v != null) fd.append(k, v);
        });
        if (fileVerb) fd.append("file", fileVerb);
        try {
            if (formVerb.id_verbale) {
                await SafetyIspettivaService.updateVerbale(fd);
            } else {
                await SafetyIspettivaService.createVerbale(fd);
            }
            setShowVerb(false);
            setMsg("Verbale salvato.");
            await load();
        } catch (err) {
            setError(apiErr(err, "Errore salvataggio verbale."));
        }
    };

    const handleDeleteVerb = async (v) => {
        if (!window.confirm("Eliminare questo verbale/prescrizione?")) return;
        try {
            await SafetyIspettivaService.deleteVerbale(v.id_verbale);
            setMsg("Verbale eliminato.");
            await load();
        } catch (err) {
            setError(apiErr(err, "Errore eliminazione."));
        }
    };

    const pct = stats?.percentuale_prontezza ?? 0;
    const pctVariant = pct >= 80 ? "success" : pct >= 50 ? "warning" : "danger";

    if (loading && !checklist.length) {
        return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;
    }

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h6 className="mb-1"><i className="bi bi-folder-symlink me-1" />Area ispettiva — pronto-verifica</h6>
                    <div className="text-muted small">
                        Checklist documentale, verbali delle verifiche e prescrizioni con scadenze di adeguamento.
                    </div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={exporting}>
                    {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-download me-1" />}
                    Esporta fascicolo
                </Button>
            </div>

            {error && <BsAlert variant="warning" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={12} md={4}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2">
                                <div className="small text-muted">Prontezza ispettiva</div>
                                <div className="fw-bold fs-5">{pct}%</div>
                                <ProgressBar now={pct} variant={pctVariant} style={{ height: 6 }} className="mt-1" />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={2}>
                        <Card className="h-100 border-0 shadow-sm"><Card.Body className="py-2 text-center">
                            <div className="small text-muted">Conformi</div>
                            <div className="fw-bold text-success">{stats.ok ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={2}>
                        <Card className="h-100 border-0 shadow-sm"><Card.Body className="py-2 text-center">
                            <div className="small text-muted">NC</div>
                            <div className="fw-bold text-danger">{stats.non_conformi ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={2}>
                        <Card className="h-100 border-0 shadow-sm"><Card.Body className="py-2 text-center">
                            <div className="small text-muted">Prescr. aperte</div>
                            <div className="fw-bold text-warning">{stats.prescrizioni_aperte ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={2}>
                        <Card className="h-100 border-0 shadow-sm"><Card.Body className="py-2 text-center">
                            <div className="small text-muted">Prescr. scadute</div>
                            <div className="fw-bold text-danger">{stats.prescrizioni_scadute ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                </Row>
            )}

            <Tabs defaultActiveKey="checklist" className="mb-3">
                <Tab eventKey="checklist" title={<span><i className="bi bi-card-checklist me-1" />Checklist</span>}>
                    <div className="table-responsive">
                        <Table hover size="sm" className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Voce</th>
                                    <th>Categoria</th>
                                    <th>Stato</th>
                                    <th>Note</th>
                                    <th style={{ width: 140 }}>Azione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checklist.map((it) => (
                                    <tr key={it.id_tipo_check}>
                                        <td>
                                            <div className="fw-semibold">{it.tipo_denominazione}</div>
                                            {it.riferimento_normativo && (
                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{it.riferimento_normativo}</div>
                                            )}
                                            {it.obbligatorio === "1" || it.obbligatorio === 1 ? (
                                                <Badge bg="light" text="dark" className="border mt-1">Obbligatorio</Badge>
                                            ) : null}
                                        </td>
                                        <td className="small">{it.categoria || "—"}</td>
                                        <td>{statoBadge(it.stato)}</td>
                                        <td>
                                            <Form.Control
                                                size="sm"
                                                value={editNotes[it.id_tipo_check] ?? it.note ?? ""}
                                                onChange={(e) => setEditNotes((p) => ({ ...p, [it.id_tipo_check]: e.target.value }))}
                                                placeholder="Note verifica…"
                                            />
                                        </td>
                                        <td>
                                            <Form.Select
                                                size="sm"
                                                value={it.stato}
                                                disabled={savingId === it.id_tipo_check}
                                                onChange={(e) => handleSaveCheck(it, e.target.value)}
                                            >
                                                {STATI_CHK.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                    </tr>
                                ))}
                                {!checklist.length && (
                                    <tr><td colSpan={5} className="text-muted text-center py-3">Nessuna voce checklist per il comparto selezionato.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Tab>

                <Tab eventKey="verbali" title={<span><i className="bi bi-file-earmark-text me-1" />Verbali e prescrizioni</span>}>
                    <div className="d-flex justify-content-end mb-2">
                        <Button size="sm" variant="primary" onClick={() => openVerbForm()}>
                            <i className="bi bi-plus-lg me-1" />Nuovo verbale
                        </Button>
                    </div>

                    {showVerb && (
                        <Card className="mb-3 border-primary">
                            <Card.Body>
                                <Form onSubmit={handleSaveVerb}>
                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Label className="small">Tipo</Form.Label>
                                            <Form.Select size="sm" value={formVerb.tipo} onChange={(e) => setFormVerb((p) => ({ ...p, tipo: e.target.value }))}>
                                                {TIPI_VERB.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Data *</Form.Label>
                                            <Form.Control size="sm" type="date" required value={formVerb.data_verbale} onChange={(e) => setFormVerb((p) => ({ ...p, data_verbale: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Ente ispettivo</Form.Label>
                                            <Form.Control size="sm" value={formVerb.ente} onChange={(e) => setFormVerb((p) => ({ ...p, ente: e.target.value }))} placeholder="ASL, INL, VVF…" />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Stato</Form.Label>
                                            <Form.Select size="sm" value={formVerb.stato} onChange={(e) => setFormVerb((p) => ({ ...p, stato: e.target.value }))}>
                                                {STATI_VERB.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="small">Oggetto</Form.Label>
                                            <Form.Control size="sm" value={formVerb.oggetto} onChange={(e) => setFormVerb((p) => ({ ...p, oggetto: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Scadenza adeguamento</Form.Label>
                                            <Form.Control size="sm" type="date" value={formVerb.data_scadenza_adeguamento} onChange={(e) => setFormVerb((p) => ({ ...p, data_scadenza_adeguamento: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Allegato PDF</Form.Label>
                                            <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFileVerb(e.target.files?.[0] || null)} />
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Descrizione / prescrizioni</Form.Label>
                                            <Form.Control as="textarea" rows={2} size="sm" value={formVerb.descrizione} onChange={(e) => setFormVerb((p) => ({ ...p, descrizione: e.target.value }))} />
                                        </Col>
                                        <Col md={12} className="d-flex gap-2 justify-content-end">
                                            <Button size="sm" variant="outline-secondary" onClick={() => setShowVerb(false)}>Annulla</Button>
                                            <Button size="sm" type="submit" variant="primary">Salva</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    <div className="table-responsive">
                        <Table hover size="sm" className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Data</th>
                                    <th>Tipo</th>
                                    <th>Ente</th>
                                    <th>Oggetto</th>
                                    <th>Scadenza</th>
                                    <th>Stato</th>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {verbali.map((v) => (
                                    <tr key={v.id_verbale}>
                                        <td>{fmtDate(v.data_verbale)}</td>
                                        <td className="small">{v.tipo_label || v.tipo}</td>
                                        <td className="small">{v.ente || "—"}</td>
                                        <td className="small">{v.oggetto || "—"}</td>
                                        <td>{scadenzaBadge(v)}</td>
                                        <td><Badge bg={v.stato === "CHIUSO" ? "success" : v.stato === "IN_CORSO" ? "warning" : "secondary"} text={v.stato === "IN_CORSO" ? "dark" : undefined}>{v.stato_label || v.stato}</Badge></td>
                                        <td className="text-nowrap">
                                            {v.allegato_presente && (
                                                <Button size="sm" variant="outline-danger" className="me-1 py-0 px-1" title="Scarica allegato"
                                                    onClick={() => SafetyIspettivaService.downloadVerbale(v.id_verbale, "verbale.pdf")}>
                                                    <i className="bi bi-file-pdf" />
                                                </Button>
                                            )}
                                            <Button size="sm" variant="outline-primary" className="me-1 py-0 px-1" onClick={() => openVerbForm(v)}><i className="bi bi-pencil" /></Button>
                                            <Button size="sm" variant="outline-secondary" className="py-0 px-1" onClick={() => handleDeleteVerb(v)}><i className="bi bi-trash" /></Button>
                                        </td>
                                    </tr>
                                ))}
                                {!verbali.length && (
                                    <tr><td colSpan={7} className="text-muted text-center py-3">Nessun verbale registrato.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
}
